// Keeps the dashboard signed in past the 15-minute access token.
// The backend sets a 7-day httpOnly refresh cookie at login; POST /api/auth/refresh
// trades it for a new access token (and rotates the cookie). Only when that fails
// does the user go back to the login page.

const LOGIN_PATH = window.location.pathname.startsWith('/admin') || window.location.pathname === '/super-admin' ? '/admin' : '/login';
const AUTH_URL = /\/auth\/(login|register|refresh|logout)\b/;
let refreshing = null;

function apiBase() {
  return (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
}

function isApiUrl(url) {
  const base = apiBase();
  return url.startsWith('/api/') || (base && url.startsWith(`${base}/api/`))
    || url.startsWith(`${window.location.origin}/api/`);
}

function logout() {
  localStorage.removeItem('token');
  localStorage.removeItem('clinicId');
  if (window.location.pathname !== LOGIN_PATH) window.location.href = LOGIN_PATH;
}

function msUntilExpiry() {
  try {
    const part = (localStorage.getItem('token') || '').split('.')[1];
    const { exp } = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return exp * 1000 - Date.now();
  } catch {
    return null;
  }
}

// One refresh at a time: the backend rotates the refresh token and treats
// reuse of an old one as theft, so concurrent refreshes must share a request.
export function refreshSession(fetchImpl = window.fetch) {
  if (!refreshing) {
    refreshing = fetchImpl(`${apiBase()}/api/auth/refresh`, { method: 'POST', credentials: 'include' })
      .then(res => (res.ok ? res.json() : Promise.reject(new Error('refresh failed'))))
      .then(data => { localStorage.setItem('token', data.access_token); return data.access_token; })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

export function installSessionRefresh(axios) {
  const nativeFetch = window.fetch.bind(window);
  const refresh = () => refreshSession(nativeFetch);

  axios.interceptors.response.use(response => response, async error => {
    const config = error.config || {};
    const status = error.response?.status;
    const url = config.url || '';
    if (status === 401 && !config._retried && !AUTH_URL.test(url) && localStorage.getItem('token')) {
      try {
        const token = await refresh();
        config._retried = true;
        config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
        return axios(config);
      } catch {
        logout();
      }
    } else if ((status === 401 || status === 403) && !AUTH_URL.test(url)) {
      logout();
    }
    return Promise.reject(error);
  });

  // Many pages call fetch directly with the token they read at render time.
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input.url;
    const response = await nativeFetch(input, init);
    if (response.status !== 401 || !isApiUrl(url) || AUTH_URL.test(url)) return response;
    const headers = new Headers(init.headers || (typeof input === 'string' ? undefined : input.headers));
    if (!(headers.get('Authorization') || '').startsWith('Bearer ')) return response;
    try {
      headers.set('Authorization', `Bearer ${await refresh()}`);
      return await nativeFetch(input, { ...init, headers });
    } catch {
      logout();
      return response;
    }
  };

  // Refresh shortly before expiry so most requests never see a 401.
  setInterval(() => {
    const ms = msUntilExpiry();
    if (ms !== null && ms < 2 * 60 * 1000) refresh().catch(() => {});
  }, 60 * 1000);
}

